import { Grid, Typography } from '@mui/material';
import { ToolbarButton } from '@tassat/button';
import { AlertTypes } from '@tassat/common-definitions';
import { PairApiConnectionForm } from '@tassat/forms';
import { Export as ExportIcon } from '@tassat/icon';
import { AlertModal, FormModal } from '@tassat/modal';
import React from 'react';

import { ApiConnectionUserCredentials, submitPairApiConnectionForm } from 'Containers/client/forms/helpers/pairApiConnectionFormFn';
import ApiConnectionsAgGrid from 'Containers/client/pages/apiConnections/grid';
import Header from 'Containers/header';
import { useAppSelector } from 'Reducers/store/hooks';
import { getApiConnectionsByClient } from 'Selectors/apiConnections';
import { getUser } from 'Selectors/common';
import { getApiTypesByUserId } from 'Selectors/userApiTypes';
import { useFeatures } from 'SharedComponents/hooks/useFeatures';
import { PageHeaderTitle } from 'SharedComponents/pageHeader';

import SecretsModal from './secretsModal';

import type { IApiConnectionsProps } from '.';

export const PAGE_TITLE = 'API Pair';

const userCredentialsInitalState = {
    nonce: '0',
    apiPairKeys: {
        publicKey: '',
        privateKey: ''
    },
    userApiKey: ''
};

export function ApiConnections(props: IApiConnectionsProps) {
    const { classes } = props;

    const user = useAppSelector(getUser);
    const apiConnections = useAppSelector(getApiConnectionsByClient);
    const userApiTypes = useAppSelector((state) => getApiTypesByUserId(state, false, state.user?.UserId || ''));
    const [features] = useFeatures();
    const [ showPairingModal, setShowPairingModal ] = React.useState<boolean>(false);
    const [ showSecretModal, setShowSecretModal ] = React.useState<boolean>(false);
    const [ exportData, setExport ] = React.useState<boolean>(false);
    const [ userCredentials, setUserCredentials ] = React.useState<ApiConnectionUserCredentials>(userCredentialsInitalState);

    const setUserCredentialsFn = (credentials: ApiConnectionUserCredentials) => {
        setUserCredentials(credentials);
        setShowSecretModal(true);
    };

    const togglePairingModal = () => {
        setShowPairingModal(!showPairingModal);
    };

    const onCloseUserCredentials = () => {
        setShowSecretModal(false);
        setUserCredentials(userCredentialsInitalState);
    };

    return (
        <Grid
            className={classes.container}
            container
            direction='column'
            spacing={0}
            wrap='nowrap'
        >
            <Grid item>
                <Header />
            </Grid>
            <Grid
                className={classes.header}
                container
                direction='row'
                item
                wrap='nowrap'
                xs={12}
            >
                <PageHeaderTitle
                    form={true}
                    onClick={() => setShowPairingModal(!showPairingModal)}
                    title={PAGE_TITLE}
                />
            </Grid>
            <Grid
                className={classes.grow}
                container
                direction='column'
                spacing={0}
                wrap='nowrap'
            >
                {apiConnections &&
                    <>
                        <Grid
                            className={classes.subtitle}
                            container
                            item
                            spacing={0}
                            xs={12}
                        >
                            <Typography
                                className={classes.subtitleText}
                                variant='body1'
                            >My Connections: {apiConnections.length}
                            </Typography>
                            <Grid item>
                                <ToolbarButton
                                    className={classes.exportButton}
                                    Icon={ExportIcon}
                                    iconClassName={classes.exportIcon}
                                    onClick={() => setExport(true)}
                                    text='Export'
                                />
                            </Grid>
                        </Grid>
                        <Grid
                            className={classes.grow}
                            item
                            xs={12}
                        >
                            <ApiConnectionsAgGrid
                                exportData={exportData}
                                features={{ fireblocks: { api: features.fireblocks.api }}}
                                rowData={apiConnections}
                                setExport={setExport}
                            />
                        </Grid>
                    </>}
            </Grid>
            <AlertModal
                Body={<SecretsModal userCredentials={userCredentials} />}
                onClose={onCloseUserCredentials}
                open={showSecretModal}
                title='Pair API'
                type={AlertTypes.message}
            />
            <FormModal
                Form={PairApiConnectionForm}
                formProps={{
                    userId: user.UserId,
                    user: user,
                    apiConnections: apiConnections,
                    apiConnectionUsers: apiConnections,
                    features: { fireblocks: { api: features.fireblocks.api }},
                    userApiTypes,
                    submitPairApiConnection: submitPairApiConnectionForm,
                    setUserCredentials: setUserCredentialsFn,
                    showBlurb: features.fireblocks.api
                }}
                onClose={togglePairingModal}
                open={showPairingModal}
                title='Pair API'
            />
        </Grid>
    );
}